import { Booking, BusinessInfo } from '../booking-service';
import { Button, gtagEvent } from '@agendor/ui';
import { ButtonGroup, ConfirmSection, ConfirmLabel, ConfirmValue } from '../shared-styles';
import { formatTime, formatSelectedDate } from '../formatting';
import * as AppRouting from '../app-routing';

type Props = {
    booking: Booking;
    services: BusinessInfo['services'];
    isBookingActionLoading: boolean;
    onConfirmBooking: () => void;
    onCancelBooking: () => void;
};

export default function BookingDetailsSection({
    booking,
    services,
    isBookingActionLoading,
    onConfirmBooking,
    onCancelBooking,
}: Props) {
    const detailsDurationMinutes = booking.services.reduce((acc, bs) => {
        const service = services.find((s) => s.id === bs.id);
        return acc + (service?.durationInMinutes ?? 0);
    }, 0);

    const needsPayment = booking.payment && !booking.payment.paid;
    const needsConfirmation = booking.confirmation?.confirmed === false && !needsPayment;

    return (
        <div style={{ paddingTop: 16 }}>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: '0 0 8px 0' }}>Detalhes do agendamento</h2>
                {needsConfirmation && (
                    <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        Você poderá comparecer a este agendamento?
                    </p>
                )}
                {needsPayment && (
                    <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                        O pagamento é necessário para confirmar o agendamento.
                    </p>
                )}
            </div>

            <div style={{ marginBottom: 24 }}>
                <ConfirmSection>
                    <ConfirmLabel>{booking.services.length > 1 ? 'Serviços' : 'Serviço'}</ConfirmLabel>
                    <ConfirmValue>{booking.services.map((s) => s.name).join(', ')}</ConfirmValue>
                </ConfirmSection>
                <ConfirmSection>
                    <ConfirmLabel>Profissional</ConfirmLabel>
                    <ConfirmValue>{booking.worker?.name ?? 'Sem preferência'}</ConfirmValue>
                </ConfirmSection>
                <ConfirmSection>
                    <ConfirmLabel>Data</ConfirmLabel>
                    <ConfirmValue>
                        {formatSelectedDate(new Date(booking.date.year, booking.date.month - 1, booking.date.day))}
                    </ConfirmValue>
                </ConfirmSection>
                <ConfirmSection>
                    <ConfirmLabel>Horário</ConfirmLabel>
                    <ConfirmValue>{formatTime(booking.time)}</ConfirmValue>
                </ConfirmSection>
                {detailsDurationMinutes > 0 && (
                    <ConfirmSection>
                        <ConfirmLabel>Duração</ConfirmLabel>
                        <ConfirmValue>{detailsDurationMinutes} minutos</ConfirmValue>
                    </ConfirmSection>
                )}
                {booking.isLinkedToSubscription && (
                    <ConfirmSection>
                        <ConfirmLabel>Vínculo</ConfirmLabel>
                        <ConfirmValue>Assinatura</ConfirmValue>
                    </ConfirmSection>
                )}
                {booking.isRecurring && (
                    <ConfirmSection>
                        <ConfirmLabel>Recorrência</ConfirmLabel>
                        <ConfirmValue>Agendamento recorrente</ConfirmValue>
                    </ConfirmSection>
                )}
                {booking.notes && (
                    <ConfirmSection>
                        <ConfirmLabel>Observações</ConfirmLabel>
                        <ConfirmValue>{booking.notes}</ConfirmValue>
                    </ConfirmSection>
                )}
            </div>

            <ButtonGroup>
                <Button
                    onClick={() => {
                        gtagEvent('booking_details_back_click');
                        AppRouting.navigate('/');
                    }}
                    disabled={isBookingActionLoading}
                >
                    Voltar
                </Button>
                <Button
                    danger
                    onClick={() => {
                        gtagEvent('booking_details_cancel_click');
                        onCancelBooking();
                    }}
                    disabled={isBookingActionLoading}
                >
                    Cancelar
                </Button>
                {needsPayment && (
                    <Button
                        primary
                        onClick={() => {
                            gtagEvent('booking_details_pay_click');
                            if (booking.payment && !booking.payment.paid)
                                window.open(booking.payment.invoiceUrl, '_blank');
                        }}
                    >
                        Pagar
                    </Button>
                )}
                {needsConfirmation && (
                    <Button primary onClick={onConfirmBooking} disabled={isBookingActionLoading}>
                        {isBookingActionLoading ? 'Confirmando...' : 'Confirmar'}
                    </Button>
                )}
            </ButtonGroup>
        </div>
    );
}
